<template>
  <div class="revenue-report">
    <h1>収益レポート</h1>

    <!-- 月選択 -->
    <div class="month-selector">
      <label for="month">対象月</label>
      <input
        id="month"
        v-model="selectedMonth"
        type="month"
        @change="fetchRevenue"
      />
    </div>

    <!-- ローディング -->
    <div v-if="loading" class="loading">
      読み込み中...
    </div>

    <!-- エラー表示 -->
    <div v-if="error" class="error">
      {{ error }}
    </div>

    <!-- 収益サマリー -->
    <div v-if="revenue && !loading && !error" class="revenue-summary">
      <div class="summary-card">
        <h3>総収益</h3>
        <p class="amount">${{ formatAmount(revenue.total_revenue) }}</p>
      </div>

      <div class="summary-card">
        <h3>総検索数</h3>
        <p class="count">{{ revenue.total_searches.toLocaleString() }} 件</p>
      </div>

      <div class="summary-card">
        <h3>データセット数</h3>
        <p class="count">{{ revenue.dataset_count }} 個</p>
      </div>
    </div>

    <!-- データセット別収益 -->
    <div v-if="revenue && revenue.datasets && revenue.datasets.length > 0" class="dataset-revenue">
      <h2>データセット別収益</h2>
      <table class="revenue-table">
        <thead>
          <tr>
            <th>データセット名</th>
            <th>検索数</th>
            <th>収益</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="dataset in revenue.datasets" :key="dataset.dataset_id">
            <td>{{ dataset.dataset_name }}</td>
            <td>{{ dataset.searches.toLocaleString() }} 件</td>
            <td>${{ formatAmount(dataset.revenue) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useDatasetStore } from '../../stores/dataset.js'

const datasetStore = useDatasetStore()

const selectedMonth = ref(getCurrentMonth())
const loading = ref(false)
const error = ref(null)
const revenue = ref(null)

function getCurrentMonth() {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  return `${year}-${month}`
}

function formatAmount(amount) {
  return parseFloat(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

async function fetchRevenue() {
  loading.value = true
  error.value = null

  try {
    const result = await datasetStore.fetchProviderRevenue(selectedMonth.value)
    revenue.value = result.data
  } catch (err) {
    error.value = err.response?.data?.error?.message || '収益データの取得に失敗しました'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchRevenue()
})
</script>

<style scoped>
.revenue-report {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.month-selector {
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.month-selector label {
  font-weight: 500;
}

.month-selector input {
  padding: 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
}

.loading,
.error {
  text-align: center;
  padding: 2rem;
  font-size: 1.1rem;
}

.error {
  color: #dc3545;
}

.revenue-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.summary-card {
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1.5rem;
  text-align: center;
}

.summary-card h3 {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  color: #6c757d;
}

.amount {
  font-size: 2rem;
  font-weight: bold;
  color: #28a745;
  margin: 0;
}

.count {
  font-size: 2rem;
  font-weight: bold;
  color: #007bff;
  margin: 0;
}

.dataset-revenue {
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 2rem;
}

.dataset-revenue h2 {
  margin: 0 0 1.5rem 0;
  font-size: 1.5rem;
}

.revenue-table {
  width: 100%;
  border-collapse: collapse;
}

.revenue-table th,
.revenue-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #dee2e6;
}

.revenue-table th {
  background-color: #f8f9fa;
  font-weight: 600;
}

.revenue-table tbody tr:hover {
  background-color: #f8f9fa;
}

.revenue-table td:nth-child(2),
.revenue-table td:nth-child(3) {
  text-align: right;
}
</style>
